import React from 'react';
import {Box, Typography, Grid, Container, Button, alpha, useTheme, Chip, Stack} from '@mui/material';
import {useAuth0} from "@auth0/auth0-react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { FREE_SUBSCRIPTION_MONTHS } from 'constants/subscription';

const PlansSection = () => {
  const {loginWithRedirect} = useAuth0();
  const {t} = useTranslation();
  const theme = useTheme();

  const plans = [
    {
      title: 'Essential',
      description: t('For Small & Boutique'),
      price: '$9',
      period: t('Monthly'),
      popular: false,
      features: [
        t('Interactive Digital Menu (1)'),
        t('Full Management Backoffice'),
        t('QR Code Generator'),
        t('Traction & Views Data'),
        t('Email Receipts'),
        t('Basic Analytics'),
      ],
    },
    {
      title: 'PLUS',
      description: t('For Small & Mid Size'),
      price: '$29',
      period: t('Monthly'),
      popular: true,
      features: [
        t('Interactive Digital Menu (1)'),
        t('Full Management Backoffice'),
        t('QR Code Generator'),
        t('Traction & Views Analysis'),
        t('Designed NFC tags (up to 30)'),
        t('Profile to Gather All Content (1)'),
        t('Re-Marketing Tracking Tool'),
        t('Customer Success Manager'),
        t('SMS Notifications'),
      ],
    },
    {
      title: 'Unlimited',
      description: t('For Mature Restaurants'),
      price: '$49',
      period: t('Monthly'),
      popular: false,
      features: [
        t('Unlimited Digital Menus'),
        t('Full Management Backoffice'),
        t('QR Code Generator'),
        t('Traction & Views Analysis'),
        t('Designed NFC tags (up to 50)'),
        t('Unlimited Profiles'),
        t('Re-Marketing Tracking Tool'),
        t('Customer Success Manager'),
        t('Custom Reports (PDF\'s, Excel, etc.)'),
        t('Priority Support'),
      ],
    },
  ];

  return (
    <Box id="pricing" sx={{py: {xs: 8, md: 12}, bgcolor: 'background.paper'}}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{textAlign: 'center', mb: 8}}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
            }}
          >
            {t("PRICING")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: '2rem', md: '3rem'},
              fontWeight: 700,
              mt: 1,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Simple, transparent pricing")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            {t("Choose the plan that fits your business. All plans include 14-day free trial.")}
          </Typography>
        </Box>

        {/* Special Promo Offer Card */}
        <Box
          sx={{
            mb: 6,
            p: {xs: 4, md: 5},
            borderRadius: '20px',
            background: `linear-gradient(135deg, 
              ${alpha(theme.palette.warning.main, 0.15)} 0%, 
              ${alpha(theme.palette.error.main, 0.15)} 100%)`,
            border: `3px solid ${theme.palette.warning.main}`,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 12px 40px ${alpha(theme.palette.warning.main, 0.3)}`,
            '&::before': {
              content: '"🎁"',
              position: 'absolute',
              top: {xs: 10, md: 20},
              right: {xs: 10, md: 20},
              fontSize: {xs: '3rem', md: '5rem'},
              opacity: 0.15,
            },
          }}
        >
          {/* Limited Time Badge */}
          <Chip
            icon={<LocalFireDepartmentIcon />}
            label={t("Special Launch Offer")}
            sx={{
              bgcolor: 'warning.main',
              color: 'white',
              fontWeight: 700,
              fontSize: {xs: '0.8rem', md: '0.9rem'},
              mb: 2,
              px: 1,
              height: 'auto',
              py: 0.5,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
              },
            }}
          />

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: 'text.primary',
                  fontSize: {xs: '1.8rem', md: '2.5rem'},
                  lineHeight: 1.2,
                }}
              >
                {t("Start Today, Pay Nothing for {{count}} Month", { count: FREE_SUBSCRIPTION_MONTHS })} 🚀
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 3,
                  lineHeight: 1.6,
                  fontSize: {xs: '1rem', md: '1.125rem'},
                }}
              >
                {t("Launch your digital menu today and enjoy full access to all QRseller features completely free for the first month. No credit card required. No hidden fees.")}
              </Typography>

              {/* Benefits List */}
              <Grid container spacing={2} sx={{mb: 3}}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{color: 'success.main', fontSize: 24}} />
                    <Typography variant="body1" sx={{fontWeight: 600}}>
                      {t("No Credit Card Required")}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{color: 'success.main', fontSize: 24}} />
                    <Typography variant="body1" sx={{fontWeight: 600}}>
                      {t("All premium features")}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{color: 'success.main', fontSize: 24}} />
                    <Typography variant="body1" sx={{fontWeight: 600}}>
                      {t("Setup in 3 minutes")}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{color: 'success.main', fontSize: 24}} />
                    <Typography variant="body1" sx={{fontWeight: 600}}>
                      {t("Cancel Anytime")}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 4,
                  borderRadius: '16px',
                  bgcolor: alpha(theme.palette.common.white, 0.9),
                  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: {xs: '3rem', md: '4rem'},
                    fontWeight: 900,
                    color: 'warning.main',
                    mb: 1,
                    lineHeight: 1,
                  }}
                >
                  1
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 3,
                  }}
                >
                  {t("1 month")}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: {xs: '2.5rem', md: '3rem'},
                    fontWeight: 900,
                    color: 'success.main',
                    mb: 3,
                  }}
                >
                  {t("Get Started Free")}
                </Typography>

                {/* CTA Button */}
                <Link
                  to="/"
                  onClick={() => loginWithRedirect({screen_hint: 'signup'})}
                  style={{textDecoration: 'none'}}
                >
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                      py: 2,
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      borderRadius: '12px',
                      bgcolor: 'warning.main',
                      color: 'white',
                      textTransform: 'none',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.4)}`,
                      '&:hover': {
                        bgcolor: 'warning.dark',
                        transform: 'translateY(-2px)',
                        boxShadow: `0 12px 32px ${alpha(theme.palette.warning.main, 0.5)}`,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {t("Claim {{count}} Free Month", { count: FREE_SUBSCRIPTION_MONTHS })} →
                  </Button>
                </Link>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 2,
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                  }}
                >
                  {t("No commitments")} • {t("Cancel Anytime")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4} alignItems="stretch">
          {plans.map((plan, index) => (
            <Grid key={index} item xs={12} md={4}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 4,
                  borderRadius: '16px',
                  bgcolor: plan.popular ? alpha(theme.palette.primary.main, 0.04) : 'background.default',
                  border: plan.popular
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, plan.popular ? 0.2 : 0.12)}`,
                  },
                }}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <Chip
                    label={t("Most Popular")}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                )}

                {/* Plan Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: 'text.primary',
                  }}
                >
                  {plan.title}
                </Typography>

                {/* Plan Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 3,
                  }}
                >
                  {plan.description}
                </Typography>

                {/* Price */}
                <Box sx={{mb: 4}}>
                  <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1}}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: 'text.primary',
                      }}
                    >
                      {plan.price}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      / {plan.period}
                    </Typography>
                  </Box>
                </Box>

                {/* Features List */}
                <Box sx={{flexGrow: 1, mb: 4}}>
                  {plan.features.map((feature, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          fontSize: 20,
                          color: 'primary.main',
                          mt: 0.2,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA Button */}
                <Link
                  to="/"
                  onClick={() => loginWithRedirect({screen_hint: 'signup'})}
                  style={{textDecoration: 'none'}}
                >
                  <Button
                    variant={plan.popular ? 'contained' : 'outlined'}
                    fullWidth
                    size="large"
                    sx={{
                      py: 1.5,
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      ...(plan.popular && {
                        boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                      }),
                    }}
                  >
                    {t("Get {{count}} Month FREE", { count: FREE_SUBSCRIPTION_MONTHS })}
                  </Button>
                </Link>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Bottom Note */}
        <Box
          sx={{
            mt: 8,
            textAlign: 'center',
            p: 4,
            borderRadius: '12px',
            bgcolor: alpha(theme.palette.info.main, 0.05),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 1,
            }}
          >
            {t("All plans include {{count}} month free. No credit card required.", { count: FREE_SUBSCRIPTION_MONTHS })}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t("Need a custom solution? Contact us for enterprise pricing.")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PlansSection;